import { noop } from "@sail/utils";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { getApolloContext, useDataContext } from "../../../internal/revamp/apollo/operationContext";
import useGraphQlDataClient from "../../../internal/revamp/config/useGraphQlDataClient";
import GqlOperationError from "../../../internal/revamp/graphql/GqlOperationError";
import addTypename from "../../../internal/revamp/queryUtils/addTypename";
import { getOperationName } from "../../../internal/shared/utils/gql";
import { skip } from "../../../internal/shared/utils/skip";
import getUnexpectedErrors from "./getUnexpectedErrors";
// Necessary so useSyncExternalStore can return a stable reference when skip is true
const fallbackResult = {
    data: null,
    errors: null,
    isPending: false
};
/**
 * @see https://sail.stripe.me/data/api/unstable_useQuery
 */ export default function useQuery(/**
   * Query options, including query document, variables and token. Can be omitted by passing `skip` from `@sail/data`.
   */ options) {
    const isSkip = options === skip;
    const queryDocument = isSkip ? undefined : options.doc;
    const queryDocWithTypename = useMemo(()=>queryDocument ? addTypename(queryDocument) : undefined, [
        queryDocument
    ]);
    const expectedErrorsRef = useRef(isSkip ? [] : options.expectedErrors);
    const component = isSkip ? undefined : options.component;
    const expectedErrorsFromOptions = isSkip ? undefined : options.expectedErrors;
    useEffect(()=>{
        expectedErrorsRef.current = isSkip ? [] : expectedErrorsFromOptions;
    }, [
        expectedErrorsFromOptions,
        isSkip
    ]);
    const operationName = isSkip || !queryDocWithTypename ? 'CouldNotFindQueryName' : getOperationName(queryDocWithTypename) || 'CouldNotFindQueryName';
    const { observableStore } = useGraphQlDataClient();
    const observable = isSkip || !queryDocWithTypename ? null : observableStore.createObservableQuery({
        document: queryDocWithTypename,
        variables: options.variables
    });
    const dataContext = useDataContext();
    const shouldUseSuspense = dataContext.shouldUseSuspense;
    // Create apolloLinkContext inside a callback to use the latest value
    // of expectedErrors while not having to include it in the dependency
    // array and cause subscribeCb/refetch instability.
    const createApolloLinkContext = useCallback(()=>getApolloContext(dataContext, {
            type: 'STANDALONE_QUERY',
            operationName,
            expectedErrors: expectedErrorsRef.current || []
        }, component), [
        component,
        dataContext,
        operationName
    ]);
    const subscribeCb = useCallback((reactCb)=>{
        const apolloLinkContext = createApolloLinkContext();
        if (isSkip || !observable) {
            return noop;
        }
        const { unsubscribe } = observable.subscribe({
            next: reactCb,
            error: reactCb
        }, apolloLinkContext);
        return unsubscribe;
    }, [
        observable,
        isSkip,
        createApolloLinkContext
    ]);
    const result = useSyncExternalStore(subscribeCb, ()=>observable ? observable.getCurrentResult() : fallbackResult);
    const refetch = useCallback(()=>{
        if (isSkip || !observable) {
            return;
        }
        const apolloLinkContext = createApolloLinkContext();
        observable.refetch(apolloLinkContext);
    }, [
        observable,
        isSkip,
        createApolloLinkContext
    ]);
    const loading = !isSkip && !result.data && !result.errors;
    if (shouldUseSuspense && loading && observable) {
        const apolloLinkContext = getApolloContext(dataContext, {
            type: 'STANDALONE_QUERY',
            operationName,
            expectedErrors: expectedErrorsFromOptions || []
        }, component);
        throw observable.startAsyncWaitOnResult(apolloLinkContext);
    }
    if (result.errors !== null) {
        const unexpectedErrors = getUnexpectedErrors(result.errors, expectedErrorsFromOptions || []);
        if (unexpectedErrors) {
            throw new GqlOperationError(unexpectedErrors);
        }
    }
    const isRefetching = result.isPending && result.data !== null;
    return {
        data: result.data,
        errors: result.errors,
        isRefetching,
        refetch
    };
}

//# sourceMappingURL=useQuery.js.map